import {
  IBuildedTour,
  IWeeklyOtaReservation,
  IWeeklyTourGroup,
  IWeeklySoundOutTemplate,
  IWeeklyFormalRequestTemplate,
  IWeeklyTourGuide,
} from './weeklyTypes'

import * as moment from 'moment'
import { IAgentPost } from '../static/interfaces'
import React from 'react'

interface IMinimalGuide {
  id: number
  fullname?: string
  username?: string
  email?: string
}

export const getInitialRequestAgentRankId = (
  tourGuide: IWeeklyTourGuide | undefined,
  messageType: 'sound_out' | 'formal'
): number | null => {
  if (!tourGuide) {
    return null
  }
  if (['agent_pre_requested', 'guide_approved'].includes(tourGuide.request_status)) {
    return tourGuide.agent_rank_id ?? null
  }
  // ツアー単位の募集の応募・見送りから正式依頼する場合は、応募先の募集の現在のランクを初期選択する
  if (
    messageType === 'formal' &&
    ['guide_applied', 'agent_rejected'].includes(tourGuide.request_status) &&
    tourGuide.individual_job
  ) {
    return tourGuide.job_agent_rank_id ?? null
  }
  return null
}

interface IMeetupPlaceData {
  area: string | null
  sub_area: string | null
  name_ja: string | null
  name_en: string | null
  meetup_point: string | null
  meetup_point_url: string | null
}

export const getDateString = (day, startTime) => {
  return moment(day).format(`YYYY/MM/DD ${startTime}`)
}

export const replaceVariableForMessage = (text, tour: IBuildedTour) => {
  return text
    .replace('[tour_name]', tour.post.name)
    .replace('[start_at]', getDateString(tour.day, tour.start_time))
}

type WeeklyTemplate = IWeeklySoundOutTemplate | IWeeklyFormalRequestTemplate

// agentRankId: null=通常ガイド。テンプレのガイド種類(agent_rank_id)が一致するものだけを返す
// (ツアーメモテンプレ等、agent_rank_idを持たないテンプレは通常扱い)
// 対象postへの紐づけはサーバ(request_templates API)側で絞り込み済み
export const getTemplateOptions = (
  templates: WeeklyTemplate[],
  agentRankId: number | null = null
) => {
  return templates
    .filter(template => (template.agent_rank_id ?? null) === agentRankId)
    .map(template => ({
      id: template.id.toString(),
      label: template.title,
      value: template.id.toString(),
    }))
}

// 選択中グループのデフォルトテンプレid
// targetPostIds はサーバが返す優先順のpost id(自post > family owner > DMCツアーのowner post)。先に一致したpostのデフォルトを採用する
export const getDefaultTemplateId = (
  templates: WeeklyTemplate[],
  targetPostIds: number[],
  agentRankId: number | null
): string | null => {
  const candidates = templates.filter(t => (t.agent_rank_id ?? null) === agentRankId)
  for (const postId of targetPostIds) {
    const found = candidates.find(t =>
      (t.default_post_ids || []).some(id => String(id) === String(postId))
    )
    if (found) {
      return found.id.toString()
    }
  }
  return null
}

export const getTemplateById = (
  id,
  selectedMessageType,
  soundoutTemplates,
  formalRequestTemplates
) => {
  if (selectedMessageType === 'sound_out') {
    const template = soundoutTemplates.find(t => t.id.toString() === id)
    if (template) {
      return {
        id: template.id.toString(),
        title: template.title,
        subject: template.mail_subject,
        message: template.message,
      }
    } else {
      return null
    }
  } else {
    const template = formalRequestTemplates.find(t => t.id.toString() === id)
    if (template) {
      return {
        id: template.id.toString(),
        title: template.title,
        subject: template.mail_subject,
        message: template.mail_text,
      }
    } else {
      return null
    }
  }
}

// utils
const safe = <T>(v: T | null | undefined, fallback = ''): any => (v == null ? fallback : v)

const calcGuestCount = (otaReservation: IWeeklyOtaReservation) =>
  (otaReservation?.num_of_adults || 0) +
  (otaReservation?.num_of_children || 0) +
  (otaReservation?.num_of_infants || 0)

const escapeForRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const buildRefNumber = (tour: IBuildedTour, guide: IMinimalGuide, tourDay: moment.Moment) => {
  if (tourDay.isValid()) {
    const dayPart = tourDay.format('YYYYMMDD')
    const timePart = (tour.start_time || '').replace(':', '').padEnd(4, '0')
    const tail = guide?.id || '000'
    return `${dayPart}-${timePart}-${tour.id}-${tail}`
  }
}

const getMeetupPlaceDataForTemplate = (
  otaReservation: IWeeklyOtaReservation
): IMeetupPlaceData | null => {
  const { meetup_place, custom_meetup_point, custom_meetup_point_url } = otaReservation

  if (!meetup_place && !custom_meetup_point && !custom_meetup_point_url) {
    return null
  }

  const meetup_point = custom_meetup_point ?? meetup_place?.meetup_point ?? null
  const meetup_point_url = custom_meetup_point_url ?? meetup_place?.meetup_point_url ?? null

  return {
    area: meetup_place?.area || null,
    sub_area: meetup_place?.sub_area || null,
    name_ja: meetup_place?.name_ja || null,
    name_en: meetup_place?.name_en || null,
    meetup_point,
    meetup_point_url,
  }
}

export function replaceVariableForMail(
  text: string,
  tour: IBuildedTour,
  guide?: IMinimalGuide,
  agent?: any,
  tourGroup?: IWeeklyTourGroup, // 原則必須だが、未選択の時にエラーになるの回避
  guideFeeAmount?: number | null, // ガイドランクに応じたpost_rank_feeの金額(未設定時はnull)
  meetingPointName?: string | null // モーダル用APIから取得する集合場所のName
): string {
  const result = text || ''

  const isPrivate = tour.post.post_type === 'private'

  const startAt = getDateString(tour.day, tour.start_time)
  const tourDay = moment(tour.day)
  const durationHours = Number(tour.post.duration)
  const startDateTime = moment(`${tour.day} ${tour.start_time}`, 'YYYY-MM-DD HH:mm')
  const endTime = startDateTime.add(durationHours, 'hours').format('HH:mm')
  const targetOtaReservations = tourGroup
    ? tour.ongoingOtaReservations.filter(
        otaReservation => otaReservation.jwt_tour_group_id === tourGroup.id
      )
    : []
  const firstOtaReservation = targetOtaReservations.length > 0 ? targetOtaReservations[0] : null

  const meetupPlace =
    isPrivate && firstOtaReservation ? getMeetupPlaceDataForTemplate(firstOtaReservation) : null

  let guestNum = firstOtaReservation ? calcGuestCount(firstOtaReservation) : 0

  // ゲスト情報はプライベートツアーのみ処理
  let guestNames = ''
  let guestInfo = ''

  if (isPrivate) {
    guestNames = targetOtaReservations
      .map(otaReservation => otaReservation.customer_full_name)
      .filter(Boolean)
      .join(', ')

    guestNum = targetOtaReservations.reduce(
      (sum: number, otaReservation: IWeeklyOtaReservation) => sum + calcGuestCount(otaReservation),
      0
    )

    // ゲスト詳細ブロック
    guestInfo = targetOtaReservations
      .map(otaReservation => {
        return [
          `- ${otaReservation.customer_full_name || '不明'}`,
          `- Adults: ${otaReservation.num_of_adults ||
            0}, Children: ${otaReservation.num_of_children ||
            0}, Infants: ${otaReservation.num_of_infants || 0}`,
          `- Other Requests: ${otaReservation.other_requests || 'なし'}`,
          `- Pickup Location: ${otaReservation.pickup_location || 'なし'}`,
        ].join('\n')
      })
      .filter(Boolean)
      .join('\n\n')
  }

  const tourType = isPrivate ? 'Private Tour' : 'Group Tour'

  const refNumber = buildRefNumber(tour, guide, tourDay)

  const replacements: Record<string, string> = {
    '[start_at]': startAt,
    '[tour_name]': safe(tour.post.name),
    '[tour_short_name]': safe(tour.post.short_name || tour.post.name),
    '[tour_full_name]': safe(tour.post.name),
    '[tour_type]': tourType,
    '[tour_date]': tourDay.isValid() ? tourDay.format('YYYY/MM/DD') : '',
    '[tour_date_full]': tourDay.isValid()
      ? tourDay
          .clone()
          .locale('ja-JP')
          .format('YYYY/MM/DD dddd')
      : '',
    '[tour_date_display]': tourDay.isValid()
      ? tourDay
          .clone()
          .locale('en-US')
          .format('Do MMM')
      : '',
    '[tour_date_display_full]': tourDay.isValid()
      ? tourDay
          .clone()
          .locale('en-US')
          .format('Do MMM YYYY (ddd)')
      : '',
    '[duration]': durationHours ? String(durationHours) : '',
    '[duration_display]': durationHours ? `${durationHours} hours` : '',
    '[meetup_time]': safe(tour.meetup_time),
    '[start_time]': safe(tour.start_time),
    '[end_time]': endTime,
    '[meeting_point_name]': safe(meetingPointName),
    '[meetup_point]': isPrivate ? safe(meetupPlace?.meetup_point, '') : '', // 過去互換
    '[meetup_place_area]': isPrivate ? safe(meetupPlace?.area, '') : '',
    '[meetup_place_sub_area]': isPrivate ? safe(meetupPlace?.sub_area, '') : '',
    '[meetup_place_name_ja]': isPrivate ? safe(meetupPlace?.name_ja, '') : '',
    '[meetup_place_name_en]': isPrivate ? safe(meetupPlace?.name_en, '') : '',
    '[meetup_place_point]': isPrivate ? safe(meetupPlace?.meetup_point, '') : '',
    '[meetup_place_url]': isPrivate ? safe(meetupPlace?.meetup_point_url, '') : '',
    '[area]': safe(tour.post.area?.name),
    '[guide_name]': safe(guide?.fullname || guide?.username),
    '[guide_nickname]': safe(guide?.username),
    '[guide_email_address]': safe(guide?.email),
    '[guide_fee]': guideFeeAmount != null ? `¥${guideFeeAmount.toLocaleString()}` : '',
    '[guest_name]': guestNames,
    '[guest_num]': guestNum.toString(),
    '[guest_info]': guestInfo,
    '[agent_name]': safe(agent?.fullname),
    '[ref_number]': refNumber,
  }

  const re = new RegExp(
    Object.keys(replacements)
      .map(escapeForRegExp)
      .join('|'),
    'g'
  )
  // 元テンプレートだけを走査し、展開した値に含まれる変数表記は再置換しない
  return result.replace(re, ph => replacements[ph])
}

export interface IFormalRequestWarning {
  message: string
  href: string
  label: string
}

// 正式依頼時の警告(送信はブロックしない): ツアーのGuide fee/Cost未設定、通常ガイドのランク未設定。
// 各警告に設定画面へのリンクを添える。打診(sound_out)では警告なし
export const getFormalRequestWarnings = ({
  messageType,
  missingPaymentSettings,
  selectedAgentRankId,
  guideHasRank,
  postId,
  guideId,
}: {
  messageType: 'sound_out' | 'formal'
  missingPaymentSettings: string[]
  selectedAgentRankId: number | null
  guideHasRank: boolean
  postId: number
  guideId: number
}): IFormalRequestWarning[] => {
  if (messageType !== 'formal') {
    return []
  }
  const tourEditPath = `/posts/${postId}/edit?active=`
  const warnings: IFormalRequestWarning[] = []
  if (missingPaymentSettings.includes('guide_fee')) {
    warnings.push({
      message: 'ツアーのGuide feeが設定されていません。',
      href: `${tourEditPath}guide_fee`,
      label: 'ツアー設定',
    })
  }
  if (missingPaymentSettings.includes('post_cost')) {
    warnings.push({
      message: 'ツアーのCostが設定されていません。',
      href: `${tourEditPath}cost`,
      label: 'ツアー設定',
    })
  }
  if (selectedAgentRankId == null && !guideHasRank) {
    warnings.push({
      message: 'ガイドのランクが設定されていません。',
      href: `/agent/guides/${guideId}/edit`,
      label: 'ガイド設定',
    })
  }
  return warnings
}

export const getPostName = (post: IAgentPost) =>
  post.short_name?.trim() || post.name || `Post ${post.id}`

const getInitialSelectedPosts = (posts: IAgentPost[], templatePosts?: IAgentPost[]) => {
  if (templatePosts) {
    return templatePosts.map(post => ({ value: String(post.id), label: getPostName(post) }))
  }

  const urlParams = new URLSearchParams(window.location.search)
  const postId = urlParams.get('post_id')

  const found = postId ? posts?.find(p => p.id === Number(postId)) : null
  return found ? [{ value: String(postId), label: getPostName(found) }] : []
}

export const useAgentPostSelection = ({
  posts,
  templatePosts,
}: {
  posts: IAgentPost[]
  templatePosts?: IAgentPost[]
}) => {
  const postOptions = posts.map(post => ({
    value: String(post.id),
    label: getPostName(post),
  }))

  // 今回の対応前に作成・編集されたものの互換で、templatePostsはフィルタしない
  const defaultPosts = getInitialSelectedPosts(posts, templatePosts)

  const [selectedPosts, setSelectedPosts] = React.useState(defaultPosts)

  return { postOptions, selectedPosts, setSelectedPosts }
}
